/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Talking to the payments endpoints, and launching the gateway's own checkout.
 *
 * Everything gateway-specific in the whole frontend is the SDK call at the
 * bottom of this file. The rest — keys, checkout, status — is the same three
 * calls whichever gateway an application is configured with.
 */

import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export const CHECKOUT_ENDPOINT = '/arcee/payments/checkout/';
export const KEY_ENDPOINT = '/arcee/payments/idempotency-keys/';
export const GATEWAY_ENDPOINT = '/arcee/payments/gateway/';
export const STATUS_ENDPOINT = '/arcee/payments/status/';
export const COUPON_VALIDATE_ENDPOINT = '/arcee/payments/coupons/validate/';

/** Cashfree's v3 SDK. Loaded once, on demand — not on every page load. */
const CASHFREE_SDK = 'https://sdk.cashfree.com/js/v3/cashfree.js';

/**
 * Mints one idempotency key for an upcoming checkout.
 *
 * Fetched when the paid lane OPENS, not when Pay is pressed: the round trip
 * then happens while the runner is still choosing seats, and costs nothing at
 * the moment they are waiting on a button.
 *
 * Returns null on failure. The caller must then refuse to start a checkout —
 * minting a key locally would defeat the registry that makes a double-click
 * safe.
 */
export async function requestIdempotencyKey({ applicationId } = {}) {
  try {
    const resp = await api.apiCall(KEY_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        application_id: Number(applicationId ?? envConfig.applicationId),
        scope: 'payments.checkout',
      }),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data?.key || null;
  } catch (err) {
    return null;
  }
}

/** Which gateway this application uses, and which environment it is in. */
export async function fetchGatewayConfig({ applicationId } = {}) {
  try {
    const appId = Number(applicationId ?? envConfig.applicationId);
    const resp = await api.apiCall(`${GATEWAY_ENDPOINT}?application_id=${appId}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    return await resp.json();
  } catch (err) {
    return null;
  }
}

/**
 * Starts a checkout. Returns {ok, payload, error, status}.
 *
 * The server's own sentence is preserved where it sent one, because the three
 * refusals that matter here each need different words from the runner's point
 * of view: 409 means their first attempt is still running, 422 means the cart
 * changed under a held key, 410 means the key aged out.
 */
export async function startCheckout(body) {
  const resp = await api.apiCall(CHECKOUT_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => null);

  if (resp.ok) return { ok: true, payload: data, status: resp.status };
  // `payload` on the failure path too, because some refusals carry FACTS the
  // caller has to show and not just a sentence: insufficient_capacity sends
  // `seats_remaining`, which is the difference between "that is full" and
  // "three are left, come back with three". Carried whole rather than
  // cherry-picked, so a new field needs no change here.
  return {
    ok: false,
    error: data?.error || null,
    detail: data?.detail || null,
    payload: data,
    status: resp.status,
  };
}

/**
 * Where a payment stands. Used by the return page while it waits.
 *
 * EITHER identifier works, because the two callers know different things. The
 * return page is told an order id by the gateway, in the URL it was redirected
 * to. A tab that came BACK from the gateway without being redirected was never
 * told anything, and has only the payment id the checkout call returned. The
 * endpoint has always accepted both; this passes on whichever is in hand.
 */
export async function fetchPaymentStatus({ applicationId, gatewayOrderId, paymentId }) {
  const appId = Number(applicationId ?? envConfig.applicationId);
  const which = gatewayOrderId
    ? `gateway_order_id=${encodeURIComponent(gatewayOrderId)}`
    : `payment_id=${encodeURIComponent(paymentId)}`;
  const query = `?application_id=${appId}&${which}`;
  const resp = await api.apiCall(`${STATUS_ENDPOINT}${query}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!resp.ok) return null;
  return await resp.json();
}

/** Loads a script once, resolving when it is ready. */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('sdk_load_failed')));
      return;
    }
    const tag = document.createElement('script');
    tag.src = src;
    tag.async = true;
    tag.addEventListener('load', () => { tag.dataset.loaded = 'true'; resolve(); });
    tag.addEventListener('error', () => reject(new Error('sdk_load_failed')));
    document.head.appendChild(tag);
  });
}

/**
 * The SDK's word for an environment, from the model's word for it.
 *
 * The model says 'live'; Cashfree's SDK wants 'production'. Getting this
 * backwards does not throw — the payment session simply fails to resolve,
 * which reads like a gateway outage rather than a config error, so it is
 * written down here rather than inline at the call site.
 */
export function cashfreeMode(environment) {
  return environment === 'live' ? 'production' : 'sandbox';
}

/**
 * Hands the runner over to the gateway's hosted page.
 *
 * The one place in this codebase that knows which gateway is in use, and the
 * seam PAYMENTS.md calls unavoidable: the SDKs are different products.
 */
export async function launchCheckout({ providerKey, environment, checkoutPayload }) {
  if (providerKey && providerKey !== 'cashfree') {
    throw new Error(`No checkout launcher for gateway '${providerKey}'.`);
  }
  const sessionId = checkoutPayload?.payment_session_id;
  if (!sessionId) throw new Error('The gateway did not return a checkout session.');

  await loadScript(CASHFREE_SDK);
  // eslint-disable-next-line no-undef
  const cashfree = Cashfree({ mode: cashfreeMode(environment) });
  return cashfree.checkout({ paymentSessionId: sessionId, redirectTarget: '_self' });
}


/**
 * Asks what a coupon code is worth against this cart. Writes nothing.
 *
 * The server resolves the code AGAIN at checkout, under a row lock, so this
 * answer is a courtesy to the runner rather than a promise to the page: by
 * the time Pay is pressed the code may have expired or been spent by somebody
 * else. Shown so nobody commits to a price they were not told.
 *
 * Returns the server's own body — { applicable, gross_amount,
 * discount_amount, payable_amount, reason? } — or null if the call could not
 * be made at all, which the caller must present as "could not check", never
 * as a refusal.
 */
export async function validateCoupon({ applicationId, couponCode, items } = {}) {
  try {
    const resp = await api.apiCall(COUPON_VALIDATE_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        application_id: Number(applicationId ?? envConfig.applicationId),
        coupon_code: couponCode,
        items,
      }),
    });
    if (!resp.ok) return null;
    return await resp.json();
  } catch (err) {
    return null;
  }
}
